import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, tap, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { trackPorCodigo } from 'src/app/services/odoo/odoo-maestros-formato';
import { OdooEstadoUi, OdooFilaComercio, OdooListaFiltro, OdooPaginaLista } from 'src/app/services/odoo/odoo-maestros.models';
import { OdooMaestrosService } from 'src/app/services/odoo/odoo-maestros.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { OdooToastService } from 'src/app/services/odoo/odoo-toast.service';
import { OdooCarga, OdooConteos, PERMISO_ODOO_ESCRIBIR } from 'src/app/services/odoo/odoo.models';
import { OdooFichaMovimiento } from 'src/app/services/odoo/odoo-ficha.models';
import { OdooFichaService } from 'src/app/services/odoo/odoo-ficha.service';
import { conCarga, mensajeDeFallo, OdooService } from 'src/app/services/odoo/odoo.service';

const fNumero = new Intl.NumberFormat('es-VE');

/** PESTAÑA COMERCIOS. Buscador (nombre/RIF) con espera de 300 ms, filtro Estado y paginación en el servidor. */
@Component({
  selector: 'app-odoo-comercios',
  templateUrl: './odoo-comercios.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OdooComerciosComponent implements OnDestroy {
  readonly limite = 20;
  filtro: OdooListaFiltro = { q: '', estado: '', comercio: '', page: 1, limit: this.limite };
  readonly buscar = new FormControl('');
  readonly estados: { valor: OdooEstadoUi | ''; texto: string }[] = [
    { valor: '', texto: 'Todos' },
    { valor: 'anotado', texto: 'Anotado' },
    { valor: 'esperando', texto: 'Esperando envío' },
    { valor: 'problema', texto: 'Con problema' },
  ];
  readonly esperaSkeleton = [0, 1, 2];
  seleccionado: OdooFilaComercio | null = null;
  pestanaSheet: 'detalle' | 'historial' = 'detalle';
  /** id del movimiento abierto en el Historial (se ve el mismo detalle que en Bitácora). */
  abierto: string | null = null;
  movs$: Observable<OdooCarga<OdooFichaMovimiento[]>> | null = null;
  private movsParam$ = new BehaviorSubject<string>('');
  private readonly refresco$ = new BehaviorSubject<void>(undefined);
  /** Cifras de comercios; si el resumen falla, la franja se oculta. */
  readonly conteos$: Observable<OdooConteos | null> = this.refresco$.pipe(
    switchMap(() => this.odoo.resumen().pipe(map((r) => r.maestros.por_tipo.comercio), catchError(() => of(null)))),
  );
  enviando = false;
  readonly trackFila = trackPorCodigo;
  readonly porIndice = (i: number): number => i;
  readonly porValor = (_: number, e: { valor: string }): string => e.valor;

  private readonly params$ = new BehaviorSubject<OdooListaFiltro>(this.filtro);
  readonly lista$: Observable<OdooCarga<OdooPaginaLista<OdooFilaComercio>>> = conCarga(this.params$, (f) => this.api.comercios(f)).pipe(
    tap((v) => this.sincronizar(v.datos?.items)),
  );
  private readonly sub: Subscription;

  constructor(
    private api: OdooMaestrosService, private ficha: OdooFichaService, private odoo: OdooService,
    public _auth: AuthService, private toast: OdooToastService, private cdr: ChangeDetectorRef,
  ) {
    this.sub = this.buscar.valueChanges.pipe(
      map((v) => String(v || '').trim()), debounceTime(300), distinctUntilChanged(),
    ).subscribe((q) => this.cambiar({ q, page: 1 }));
  }

  ngOnDestroy(): void { this.sub.unsubscribe(); }

  /** Si el panel está abierto, lo pone al día con la fila recién traída de la lista. */
  private sincronizar(items: OdooFilaComercio[] | undefined): void {
    const id = this.seleccionado?.source_id;
    const nuevo = id && items ? items.find((x) => x.source_id === id) : null;
    if (nuevo) { this.seleccionado = nuevo; this.cdr.markForCheck(); }
  }

  private cambiar(parcial: Partial<OdooListaFiltro>): void {
    this.filtro = { ...this.filtro, ...parcial };
    this.params$.next(this.filtro);
  }

  elegirEstado(valor: string): void { this.cambiar({ estado: valor as OdooEstadoUi | '', page: 1 }); }
  pagina(n: number): void { this.cambiar({ page: n }); }
  recargar(): void { this.params$.next({ ...this.filtro }); }

  numero(n: number): string { return fNumero.format(n); }
  get puedeEditar(): boolean { return this._auth.hasPermission(PERMISO_ODOO_ESCRIBIR); }
  total(k: OdooConteos): number { return k.anotados + k.pendientes + k.errores; }
  alternar(id: string): void { this.abierto = this.abierto === id ? null : id; }
  verTodo(): void { this.pestanaSheet = 'historial'; }
  /** Solo la vista: quita la secuencia literal «\n» y los espacios sobrantes del nombre. */
  limpiar(n: string | null | undefined): string { return (n || '').replace(/\\n/g, ' ').replace(/\s+/g, ' ').trim(); }

  elegir(f: OdooFilaComercio): void {
    this.seleccionado = f;
    this.pestanaSheet = 'detalle';
    this.abierto = null;
    this.movsParam$ = new BehaviorSubject(f.source_id);
    this.movs$ = conCarga(this.movsParam$, (code) => this.ficha.movimientos(code, 30, 'comercio'));
  }
  cerrar(): void { this.seleccionado = null; this.movs$ = null; }

  /** Vuelve a enviar este comercio a Odoo; al terminar avisa, refresca lista, cifras y movimientos. */
  reenviar(): void {
    const c = this.seleccionado;
    if (!c || this.enviando || !this.puedeEditar) return;
    this.enviando = true;
    this.odoo.reenviar('comercio', c.source_id).subscribe({
      next: (r) => {
        this.enviando = false;
        this.toast.mostrar(r.estado === 'error' ? (r.ultimo_error || 'Odoo no aceptó el envío.') : 'Comercio enviado a Odoo.', r.estado !== 'error');
        this.recargar();
        this.refresco$.next();
        this.movsParam$.next(c.source_id);
        this.cdr.markForCheck();
      },
      error: (e: unknown) => { this.enviando = false; this.toast.mostrar(mensajeDeFallo(e), false); this.cdr.markForCheck(); },
    });
  }
  readonly porMov = (_: number, m: OdooFichaMovimiento): string => m.id;
}
